/**
 * Auth Service - Authentication logic for admin and residents
 * Enhanced with security features and audit logging
 */

import {
    auth, 
    signInWithEmailAndPassword, 
    signOut, 
    onAuthStateChanged, 
    setPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
    APP_CONFIG,
    logSecurityEvent,
    verifyAdminRole,
    getUserClaims,
    db,
    COLLECTIONS
} from '../../firebase-config.js';
import { AppState } from '../../modules/state.js';
import { eventBus, EVENTS } from '../../core/events.js';
import { toastSuccess, toastError } from '../../shared/ui/toast.js';
import { doc, setDoc, serverTimestamp, increment } from 'firebase/firestore';
import type { AdminUser, ResidentUser, CurrentUser, LoginAttempt } from './auth.types.js';

// Login attempt tracking (client-side only, for UI feedback)
const loginAttempts = new Map<string, LoginAttempt>();
const RESIDENT_AUTH_EMAIL_DOMAIN = 'apartmanyonetimi.local';

function isValidResidentApartmentNumber(apartmentNumber: number): boolean {
    return Number.isInteger(apartmentNumber) &&
        apartmentNumber >= 1 &&
        apartmentNumber <= APP_CONFIG.TOTAL_APARTMENTS;
}

function getResidentLoginIdentifier(apartmentNumber: number): string {
    return `resident:${apartmentNumber}`;
}

function getResidentAuthEmail(apartmentNumber: number): string {
    return `resident.apt${apartmentNumber}@${RESIDENT_AUTH_EMAIL_DOMAIN}`;
}

function createResidentSession(apartmentNumber: number, uid?: string, email?: string | null, loginAt?: string): ResidentUser {
    const startedAt = loginAt || new Date().toISOString();
    const expiry = new Date(Date.now() + (24 * 60 * 60 * 1000)).toISOString();

    return {
        role: 'resident',
        apartment: apartmentNumber,
        uid,
        email: email || getResidentAuthEmail(apartmentNumber),
        loginAt: startedAt,
        sessionExpiry: expiry
    };
}

function persistResidentSession(residentUser: ResidentUser): void {
    sessionStorage.setItem(
        APP_CONFIG.SESSION_STORAGE_KEY,
        JSON.stringify(residentUser)
    );
}

function readResidentSession(): ResidentUser | null {
    const rawValue = sessionStorage.getItem(APP_CONFIG.SESSION_STORAGE_KEY);
    if (!rawValue) return null;

    try {
        return JSON.parse(rawValue) as ResidentUser;
    } catch {
        sessionStorage.removeItem(APP_CONFIG.SESSION_STORAGE_KEY);
        return null;
    }
}

function isResidentSessionExpired(residentUser: ResidentUser | null): boolean {
    if (!residentUser) return false;

    if (residentUser.sessionExpiry) {
        return Date.now() > new Date(residentUser.sessionExpiry).getTime();
    }

    if (residentUser.loginAt) {
        return Date.now() > new Date(residentUser.loginAt).getTime() + (24 * 60 * 60 * 1000);
    }

    return false;
}

function hasResidentClaim(claims: Record<string, any>): claims is { role: 'resident'; apartmentNumber: number } {
    return claims.role === 'resident' && isValidResidentApartmentNumber(Number(claims.apartmentNumber));
}

/**
 * Get or initialize login attempt tracking for an identifier
 */
function getLoginAttempt(identifier: string): LoginAttempt {
    if (!loginAttempts.has(identifier)) {
        loginAttempts.set(identifier, {
            count: 0,
            lastAttempt: 0,
            locked: false
        });
    }
    return loginAttempts.get(identifier)!;
}

/**
 * Check if account is locked due to failed attempts
 */
function isAccountLocked(identifier: string): boolean {
    const attempt = getLoginAttempt(identifier);
    if (!attempt.locked) return false;
    
    const lockoutDuration = APP_CONFIG.SECURITY.lockoutDurationMinutes * 60 * 1000;
    const timeSinceLock = Date.now() - attempt.lastAttempt;
    
    if (timeSinceLock > lockoutDuration) {
        // Reset lock after duration
        attempt.locked = false;
        attempt.count = 0;
        return false;
    }
    
    return true;
}

/**
 * Record a failed login attempt
 */
function recordFailedAttempt(identifier: string): void {
    const attempt = getLoginAttempt(identifier);
    attempt.count++;
    attempt.lastAttempt = Date.now();
    
    if (attempt.count >= APP_CONFIG.SECURITY.maxLoginAttempts) {
        attempt.locked = true;
        console.warn(`🔒 Account locked for ${identifier} due to multiple failed attempts`);
    }
}

/**
 * Reset login attempts on successful login
 */
function resetLoginAttempts(identifier: string): void {
    loginAttempts.delete(identifier);
}

/**
 * Uygulamadaki class tabanli sayfa gecisi ile uyumlu sekilde sayfa gosterir.
 * Router modulu inline display kullandigi icin logout sonrasi login layout'u kayabiliyor.
 */
function activatePage(pageId: string): void {
    document.querySelectorAll('.page').forEach(page => {
        (page as HTMLElement).style.display = '';
        page.classList.remove('active');
    });

    const target = document.getElementById(pageId);
    if (target) {
        (target as HTMLElement).style.display = '';
        target.classList.add('active');
        AppState.currentPage = pageId;
    }
}

/**
 * Logout sonrasi acik mobil menuler/modallar kalmamasi icin UI state temizligi.
 */
function resetUiStateAfterLogout(): void {
    document.body.style.overflow = '';

    ['nav-links', 'resident-nav-links'].forEach(menuId => {
        const menu = document.getElementById(menuId);
        if (menu) menu.classList.remove('active');
    });

    ['mobile-menu-toggle', 'resident-mobile-menu-toggle'].forEach(toggleId => {
        const toggle = document.getElementById(toggleId);
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
}

/**
 * Auth Service - Handles all authentication operations
 */
export const AuthService = {
    /**
     * Login as admin using Firebase Authentication
     * Enhanced with rate limiting and audit logging
     */
    async loginAdmin(password: string, emailOverride?: string): Promise<boolean> {
        const email = (emailOverride || APP_CONFIG.ADMIN_EMAIL || '').trim().toLowerCase();
        const deviceId = this.getDeviceFingerprint();

        if (!email) {
            toastError('Yönetici e-posta adresi gerekli.');
            return false;
        }
        
        // Check rate limiting
        if (isAccountLocked(email)) {
            const attempt = getLoginAttempt(email);
            const remainingMinutes = Math.ceil(
                (APP_CONFIG.SECURITY.lockoutDurationMinutes * 60 * 1000 - 
                 (Date.now() - attempt.lastAttempt)) / 60000
            );
            toastError(`Çok fazla başarısız giriş denemesi. Lütfen ${remainingMinutes} dakika sonra tekrar deneyin.`);
            return false;
        }

        try {
            await setPersistence(auth, browserLocalPersistence);

            // Log attempt
            await logSecurityEvent('login_attempt', {
                email,
                method: 'password',
                deviceId
            });

            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Verify admin role in Firestore (additional security layer)
            const isVerifiedAdmin = await verifyAdminRole(user.uid);
            
            // Get custom claims
            const claims = await getUserClaims(user);
            
            // Check if user has admin claim OR is in admins collection
            if (!claims.admin && !isVerifiedAdmin) {
                // Not an admin - sign out immediately
                await signOut(auth);
                
                await logSecurityEvent('unauthorized_access', {
                    email,
                    uid: user.uid,
                    reason: 'User authenticated but not admin'
                }, user.uid);
                
                toastError('Bu hesap yönetici yetkisine sahip değil.');
                recordFailedAttempt(email);
                return false;
            }

            // If verified in Firestore but no claim, update claim locally
            // (In production, this should be done via Firebase Admin SDK on server)
            if (isVerifiedAdmin && !claims.admin) {
                console.warn('Admin verified in Firestore but missing custom claim. Consider updating via Admin SDK.');
            }

            // Success - reset attempts
            resetLoginAttempts(email);

            const adminUser: AdminUser = {
                role: 'admin',
                uid: user.uid,
                email: user.email,
                isVerified: true,
                lastLoginAt: new Date().toISOString()
            };

            AppState.currentUser = adminUser;

            // Update last login in Firestore
            await this.updateAdminLastLogin(user.uid);

            // Log success
            await logSecurityEvent('login_success', {
                email,
                uid: user.uid,
                deviceId
            }, user.uid);

            eventBus.emit(EVENTS.AUTH_LOGIN, adminUser);
            toastSuccess('Hoş geldiniz, Yönetici!');

            return true;
        } catch (error: any) {
            console.error("Login error:", error);
            
            recordFailedAttempt(email);
            const attempt = getLoginAttempt(email);
            const remainingAttempts = APP_CONFIG.SECURITY.maxLoginAttempts - attempt.count;
            
            // Log failure
            await logSecurityEvent('login_failure', {
                email,
                error: error.code,
                deviceId,
                remainingAttempts
            });

            // User-friendly error messages
            let errorMessage = 'Giriş başarısız! Lütfen e-posta ve parolanızı kontrol edin.';
            
            if (error.code === 'auth/wrong-password') {
                errorMessage = `Hatalı şifre. Kalan deneme hakkı: ${remainingAttempts}`;
            } else if (error.code === 'auth/user-not-found') {
                errorMessage = 'Kullanıcı bulunamadı.';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = 'Çok fazla deneme yapıldı. Lütfen daha sonra tekrar deneyin.';
            } else if (error.code === 'auth/invalid-credential') {
                errorMessage = `Geçersiz giriş bilgileri. Kalan deneme hakkı: ${remainingAttempts}`;
            }
            
            toastError(errorMessage);
            return false;
        }
    },

    /**
     * Update admin's last login timestamp
     */
    async updateAdminLastLogin(uid: string): Promise<void> {
        try {
            const adminRef = doc(db, COLLECTIONS.ADMINS, uid);
            await setDoc(adminRef, {
                lastLoginAt: serverTimestamp(),
                lastLoginIp: 'client-side', // In production, get from server
                loginCount: increment(1)
            }, { merge: true });
        } catch (e) {
            console.error('Failed to update last login:', e);
        }
    },

    /**
     * Generate a simple device fingerprint for tracking
     */
    getDeviceFingerprint(): string {
        const userAgent = navigator.userAgent;
        const screenRes = `${screen.width}x${screen.height}`;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const fingerprint = btoa(`${userAgent}-${screenRes}-${timezone}`).slice(0, 32);
        return fingerprint;
    },

    /**
     * Login as resident using apartment-specific Firebase Auth credentials
     */
    async loginResident(apartmentNumber: number, password?: string): Promise<boolean> {
        if (!isValidResidentApartmentNumber(apartmentNumber)) {
            toastError('Geçersiz daire numarası!');
            return false;
        }

        const identifier = getResidentLoginIdentifier(apartmentNumber);
        const residentEmail = getResidentAuthEmail(apartmentNumber);
        const trimmedPassword = String(password || '').trim();

        if (trimmedPassword.length < 6) {
            toastError('Sakin şifresi en az 6 karakter olmalıdır.');
            return false;
        }

        if (isAccountLocked(identifier)) {
            const attempt = getLoginAttempt(identifier);
            const remainingMinutes = Math.ceil(
                (APP_CONFIG.SECURITY.lockoutDurationMinutes * 60 * 1000 -
                    (Date.now() - attempt.lastAttempt)) / 60000
            );
            toastError(`Çok fazla başarısız giriş denemesi. Lütfen ${remainingMinutes} dakika sonra tekrar deneyin.`);
            return false;
        }

        try {
            await setPersistence(auth, browserSessionPersistence);

            await logSecurityEvent('login_attempt', {
                apartmentNumber,
                method: 'resident_password'
            });

            const userCredential = await signInWithEmailAndPassword(auth, residentEmail, trimmedPassword);
            const user = userCredential.user;
            const claims = await getUserClaims(user);

            if (!hasResidentClaim(claims) || Number(claims.apartmentNumber) !== apartmentNumber) {
                await signOut(auth);

                await logSecurityEvent('unauthorized_access', {
                    apartmentNumber,
                    uid: user.uid,
                    reason: 'Resident authenticated without matching apartment claim'
                }, user.uid);

                recordFailedAttempt(identifier);
                toastError('Bu daire için giriş yetkisi doğrulanamadı.');
                return false;
            }

            resetLoginAttempts(identifier);

            const residentUser = createResidentSession(apartmentNumber, user.uid, user.email);

            AppState.currentUser = residentUser;
            persistResidentSession(residentUser);

            await logSecurityEvent('login_success', {
                apartmentNumber,
                uid: user.uid,
                method: 'resident_password'
            }, user.uid);

            eventBus.emit(EVENTS.AUTH_LOGIN, residentUser);
            toastSuccess(`Hoş geldiniz, Daire ${apartmentNumber}!`);

            return true;
        } catch (error: any) {
            console.error('Resident login error:', error);

            recordFailedAttempt(identifier);
            const attempt = getLoginAttempt(identifier);
            const remainingAttempts = APP_CONFIG.SECURITY.maxLoginAttempts - attempt.count;

            await logSecurityEvent('login_failure', {
                apartmentNumber,
                error: error.code,
                remainingAttempts,
                method: 'resident_password'
            });

            let errorMessage = 'Daire numarası veya şifre hatalı.';

            if (error.code === 'auth/user-disabled') {
                errorMessage = 'Bu daire için sakin girişi pasifleştirilmiş durumda. Yönetici ile iletişime geçin.';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = 'Çok fazla deneme yapıldı. Lütfen daha sonra tekrar deneyin.';
            } else if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
                errorMessage = `Daire numarası veya şifre hatalı. Kalan deneme hakkı: ${Math.max(remainingAttempts, 0)}`;
            }

            toastError(errorMessage);
            return false;
        }
    },

    /**
     * Logout current user
     */
    async logout(): Promise<void> {
        try {
            const previousUser = AppState.currentUser;
            
            await signOut(auth);
            sessionStorage.removeItem(APP_CONFIG.SESSION_STORAGE_KEY);
            
            AppState.currentUser = null;

            // Log logout
            if (previousUser?.uid) {
                await logSecurityEvent('data_access', {
                    action: 'logout',
                    userRole: previousUser.role
                }, previousUser.uid);
            }

            eventBus.emit(EVENTS.AUTH_LOGOUT, previousUser);
            resetUiStateAfterLogout();
            activatePage('login-page');
            toastSuccess('Çıkış yapıldı');
        } catch (error) {
            console.error("Logout error", error);
            toastError('Çıkış yapılırken bir hata oluştu.');
        }
    },

    /**
     * Check and restore authentication state
     */
    checkAuth(onAuthenticated?: (user: CurrentUser) => void): void {
        // Listen to Firebase Auth State (for admin)
        onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Admin is logged in - verify role
                const claims = await getUserClaims(user);
                const isVerifiedAdmin = await verifyAdminRole(user.uid);
                
                if (claims.admin || isVerifiedAdmin) {
                    const adminUser: AdminUser = {
                        role: 'admin',
                        uid: user.uid,
                        email: user.email,
                        isVerified: true,
                        lastLoginAt: new Date().toISOString()
                    };
                    
                    AppState.currentUser = adminUser;
                    eventBus.emit(EVENTS.AUTH_STATE_CHANGED, adminUser);
                    onAuthenticated?.(adminUser);
                } else if (hasResidentClaim(claims)) {
                    const savedResident = readResidentSession();

                    if (isResidentSessionExpired(savedResident)) {
                        sessionStorage.removeItem(APP_CONFIG.SESSION_STORAGE_KEY);
                        await signOut(auth);
                        eventBus.emit(EVENTS.AUTH_STATE_CHANGED, null);
                        return;
                    }

                    const apartmentNumber = Number(claims.apartmentNumber);
                    const residentUser = createResidentSession(
                        apartmentNumber,
                        user.uid,
                        user.email,
                        savedResident?.apartment === apartmentNumber ? savedResident.loginAt : undefined
                    );

                    AppState.currentUser = residentUser;
                    persistResidentSession(residentUser);
                    eventBus.emit(EVENTS.AUTH_STATE_CHANGED, residentUser);
                    onAuthenticated?.(residentUser);
                } else {
                    // User authenticated but not recognized
                    console.warn('User authenticated but not recognized, signing out');
                    await signOut(auth);
                    eventBus.emit(EVENTS.AUTH_STATE_CHANGED, null);
                }
            } else {
                sessionStorage.removeItem(APP_CONFIG.SESSION_STORAGE_KEY);
                AppState.currentUser = null;
                eventBus.emit(EVENTS.AUTH_STATE_CHANGED, null);
            }
        });
    },

    /**
     * Get current user
     */
    getCurrentUser(): CurrentUser {
        return AppState.currentUser;
    },

    /**
     * Check if user is admin
     */
    isAdmin(): boolean {
        return AppState.currentUser?.role === 'admin';
    },

    /**
     * Check if user is resident
     */
    isResident(): boolean {
        return AppState.currentUser?.role === 'resident';
    },

    /**
     * Get resident apartment number (if resident)
     */
    getResidentApartment(): number | null {
        const user = AppState.currentUser;
        if (user?.role === 'resident') {
            return user.apartment;
        }
        return null;
    },

    /**
     * Require admin access - throws error if not admin
     */
    requireAdmin(): void {
        if (!this.isAdmin()) {
            throw new Error('Admin access required');
        }
    },

    /**
     * Check if user can access apartment data
     */
    canAccessApartment(apartmentNumber: number): boolean {
        if (this.isAdmin()) return true;
        if (this.isResident()) {
            return this.getResidentApartment() === apartmentNumber;
        }
        return false;
    }
};

// Export convenience functions for backward compatibility
export const loginAdmin = AuthService.loginAdmin.bind(AuthService);
export const loginResident = AuthService.loginResident.bind(AuthService);
export const logout = AuthService.logout.bind(AuthService);
export const checkAuth = AuthService.checkAuth.bind(AuthService);

export default AuthService;
